import { FC, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { 
  Brain, 
  Target, 
  Shield, 
  ArrowRight,
  Sparkles,
  Clock
} from 'lucide-react';
import { VideoHero } from '@/components/landing/VideoHero';
import { Testimonials } from '@/components/landing/Testimonials';
import { FAQ } from '@/components/landing/FAQ';
import { OurStory } from '@/components/landing/OurStory';
import { CTASection } from '@/components/landing/CTASection';
import { PricingSection } from '@/components/landing/PricingSection';
import { Features } from '@/components/landing/Features';
import { WhyChooseUs } from '@/components/landing/WhyChooseUs';
import { PlatformFeatures } from '@/components/landing/PlatformFeatures';
import { TeachingMethodology } from '@/components/landing/TeachingMethodology';
import { usePageVisit } from '@/hooks/usePageVisit';
import { FeatureGrid } from '@/components/landing/FeatureGrid';
import { BrandHighlight } from '@/components/landing/BrandHighlight';
import { ParentalControl } from '@/components/landing/ParentalControl';

const Index: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  usePageVisit();

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('referralCode', refCode);
    }
  }, [searchParams]);

  const handleStartTrial = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-accent/5">
      {/* Header/Nav */}
      <nav className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="w-[250px] sm:w-[250px] h-[80px] sm:h-[80px] relative flex items-center">
          <img 
            src="https://media.financieramente.kids/logo-financieramente.png" 
            alt="FinancieraMente" 
            className="w-full h-full object-contain"
            loading="eager"
            decoding="async"
            priority="high"
          />
        </div>
        <div className="flex gap-2 sm:gap-4">
          <Button 
            variant="ghost"
            onClick={() => navigate('/login')}
            className="text-sm sm:text-base"
          >
            Iniciar sesión
          </Button>
          <Button 
            variant="default"
            onClick={handleStartTrial}
            className="bg-[#22c55e] hover:bg-[#16a34a] text-sm sm:text-base"
          >
            Prueba Gratuita
          </Button>
        </div>
      </nav>

      {/* Hero Section with Video */}
      <section className="container mx-auto px-4 py-8 md:py-16">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary mb-6">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs sm:text-sm font-medium">Educación financiera para la nueva generación</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent leading-tight">
            Prepara a tus hijos para un futuro financiero exitoso
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto px-4">
            La educación financiera y emprendedora que la escuela no enseña, 
            pero que definirá el éxito de nuestros hijos en el mundo real
          </p>

          <VideoHero />

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-2 px-4">
            <Button
              size="lg"
              className="bg-[#22c55e] hover:bg-[#16a34a] text-white text-base sm:text-lg px-6 sm:px-8 h-12"
              onClick={handleStartTrial}
            >
              Comenzar Prueba Gratuita
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <p className="text-xs sm:text-sm text-gray-500 flex items-center justify-center gap-2 mt-2">
            <Clock className="h-4 w-4" />
            Prueba gratuita por 7 días, cancela cuando quieras
          </p>
        </div>
      </section>

      {/* Brand Highlight Section */}
      <BrandHighlight />

      {/* Features Grid Section */}
      <FeatureGrid />

      {/* Features Section */}
      <Features />

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Platform Features Section */}
      <PlatformFeatures />

      {/* Parental Control Section */}
      <ParentalControl />

      {/* Teaching Methodology Section */}
      <TeachingMethodology />

      {/* Our Story Section */}
      <OurStory />

      {/* Testimonials Section */}
      <Testimonials />

      {/* Pricing Section */}
      <PricingSection />

      {/* FAQ Section */}
      <FAQ />

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Index;